/**
 * E2E-74: the single table of common WhatsApp Cloud (Meta) delivery/send error codes with a plain
 * Arabic + English reason and a how-to-fix line. Reused by the inbox failed bubble, the broadcast
 * report and the channel health card. Pure; no I/O.
 */
export interface MetaFailureText {
  readonly ar: string;
  readonly en: string;
}

export interface MetaFailureInfo {
  readonly code: string;
  readonly reason: MetaFailureText;
  readonly fix: MetaFailureText;
}

const row = (
  reasonAr: string,
  reasonEn: string,
  fixAr: string,
  fixEn: string,
): Omit<MetaFailureInfo, "code"> => ({
  reason: { ar: reasonAr, en: reasonEn },
  fix: { ar: fixAr, en: fixEn },
});

const TEMPLATE_FIX = row(
  "مشكلة في القالب",
  "Template problem",
  "راجع القالب في ميتا (الاسم واللغة والمتغيرات والاعتماد) ثم زامن القوالب.",
  "Check the template in Meta (name, language, variables, approval) and sync templates.",
);

export const META_FAILURE_TABLE: Readonly<Record<string, Omit<MetaFailureInfo, "code">>> = {
  "131042": row(
    "حساب ميتا يحتاج طريقة دفع",
    "your Meta account needs a payment method",
    "أضف بطاقة في إعدادات الدفع لحساب واتساب للأعمال ثم أعد الإرسال.",
    "Add a card in the payment settings of your WhatsApp Business account, then resend.",
  ),
  "131047": row(
    "انتهت نافذة الـ24 ساعة",
    "The 24-hour window is closed",
    "أرسل قالباً معتمداً بدلاً من رسالة حرة",
    "Send an approved template instead of a free-form message",
  ),
  "131026": row(
    "تعذّر التسليم: الرقم غير مسجل في واتساب أو لا يقبل الرسائل",
    "Undeliverable: the number is not on WhatsApp or cannot receive this message",
    "تأكد من صحة الرقم وأن العميل يستخدم واتساب بنسخة حديثة",
    "Check the number is correct and the customer uses an up-to-date WhatsApp",
  ),
  "131049": row(
    "ميتا لم تسلّم الرسالة التسويقية لتحديد عدد الرسائل التسويقية للعميل",
    "Meta did not deliver this marketing message (per-customer marketing limit)",
    "انتظر قبل إعادة المحاولة وقلّل عدد الرسائل التسويقية لنفس العميل",
    "Wait before retrying and send fewer marketing messages to the same customer",
  ),
  "131056": row(
    "عدد كبير من الرسائل لنفس العميل في وقت قصير",
    "Too many messages to the same customer too quickly",
    "انتظر قليلاً ثم أعد المحاولة",
    "Wait a short while and retry",
  ),
  "130472": row(
    "الرقم ضمن تجربة لدى ميتا ولن تُسلَّم الرسالة",
    "The number is part of a Meta experiment, so the message is not delivered",
    "لا إجراء مطلوب؛ جرّب لاحقاً أو تواصل مع العميل بقناة أخرى",
    "No action needed; retry later or reach the customer another way",
  ),
  "132000": row(
    "عدد متغيرات القالب لا يطابق المُرسَل",
    "Template variable count does not match",
    "أرسل عدد القيم نفسه الموجود في القالب",
    "Provide exactly as many values as the template has variables",
  ),
  "132001": row(
    "القالب غير موجود بهذه اللغة",
    "Template does not exist in this language",
    "تأكد من اسم القالب ولغته واعتماده ثم زامن القوالب من ميتا",
    "Check the template name, language and approval, then sync templates from Meta",
  ),
  "132005": row(
    "نص القالب بعد التعبئة طويل جداً",
    "The filled-in template text is too long",
    "اختصر قيم المتغيرات أو نص القالب",
    "Shorten the variable values or the template text",
  ),
  "132007": row(
    "أوقفت ميتا القالب لمخالفة السياسات",
    "Meta paused the template for a policy violation",
    "عدّل المحتوى وفق سياسات واتساب ثم أعد تقديمه",
    "Edit the content to comply with WhatsApp policy and resubmit",
  ),
  "132012": row(
    "تنسيق قيمة متغير لا يطابق القالب",
    "A variable value has an invalid format",
    "أزل الأسطر الجديدة والتبويبات والمسافات المتكررة من القيم",
    "Remove new lines, tabs and repeated spaces from the values",
  ),
  "132015": row(
    "القالب موقوف مؤقتاً بسبب جودته",
    "The template is paused due to low quality",
    "انتظر انتهاء الإيقاف أو استخدم قالباً معتمداً آخر",
    "Wait until the pause ends or use another approved template",
  ),
  "132016": row(
    "القالب معطّل نهائياً",
    "The template is disabled",
    "أنشئ قالباً جديداً بمحتوى مختلف",
    "Create a new template with different content",
  ),
  "100": row(
    "معامل غير صالح في الطلب",
    "Invalid parameter in the request",
    "راجع بيانات الرسالة (الرقم والقالب والوسائط) ثم أعد المحاولة",
    "Review the message data (number, template, media) and retry",
  ),
  "190": row(
    "انتهت صلاحية ربط واتساب أو أصبحت غير صالحة",
    "The WhatsApp connection token expired or is invalid",
    "أعد ربط القناة من صفحة القنوات",
    "Reconnect the channel from the Channels page",
  ),
  "368": row(
    "حظرت ميتا الحساب مؤقتاً بسبب مخالفة السياسات",
    "Meta temporarily blocked the account for a policy violation",
    "راجع جودة الحساب في مدير أعمال ميتا وتقدّم بطلب مراجعة",
    "Check account quality in Meta Business Manager and request a review",
  ),
  "80007": row(
    "تجاوز الحساب حد الطلبات لدى ميتا",
    "The account hit Meta's rate limit",
    "انتظر قليلاً؛ ستتم إعادة المحاولة تلقائياً",
    "Wait a little; sending will retry automatically",
  ),
};

/** Extracts the numeric Meta code from a stored code ("131042") or reason ("131042: Business…"). */
export function metaCodeFromText(text: string | null | undefined): string | null {
  if (!text) return null;
  const m = /^\s*(?:Meta\s+)?(\d{1,6})\b/i.exec(text);
  return m?.[1] ?? null;
}

/** Plain reason + how-to-fix for a Meta code or raw "code: message" reason; null when unmapped. */
export function describeMetaFailure(
  input: string | number | null | undefined,
): MetaFailureInfo | null {
  const code = typeof input === "number" ? String(input) : metaCodeFromText(input);
  if (!code) return null;
  const hit =
    META_FAILURE_TABLE[code] ??
    (Number(code) >= 132000 && Number(code) < 133000 ? TEMPLATE_FIX : undefined);
  return hit ? { code, ...hit } : null;
}

/** Bilingual one-liner for DB/log text: "<reason> · <fix>" in the given locale. */
export function metaFailureLine(info: MetaFailureInfo, locale: "ar" | "en"): string {
  return `${info.reason[locale]} — ${info.fix[locale]}`;
}
